// src/pages/Perfil/Perfil.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Paper, Typography, TextField, Button, Grid, Divider, IconButton,
    InputAdornment, CircularProgress, Avatar, Chip, Box, Collapse,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import WorkIcon from '@mui/icons-material/Work';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import PeopleIcon from '@mui/icons-material/People';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DownloadIcon from '@mui/icons-material/Download';
import { useNotification } from '../../contexts/NotificationContext';
import { cambiarPasswordJWT } from '../../api/endpoints';
import { nodeApiPath } from '../../utils/paths';
import MisLicencias from './MisLicencias';
import MiEquipo from './MiEquipo';
import MisTareasSeccion from './MisTareasSeccion';
import VistaEjecutiva from './VistaEjecutiva';
import FotoCropDialog from '../../components/FotoCropDialog';
import PdfPreviewDialog from '../../components/CargaMasiva/PdfPreviewDialog';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const BR = '4px';
const PRIMARY = '#1769aa';

const capitalize = (s) => (s || '').toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
const fmtCUIL = (c) => {
    if (!c) return '—';
    const d = c.toString().replace(/\D/g, '');
    return d.length === 11 ? `${d.substring(0, 2)}-${d.substring(2, 10)}-${d.substring(10)}` : c;
};
const fmtPhone = (p) => p || '—';
const getInitials = (n) => {
    if (!n) return '?';
    const parts = n.trim().split(' ');
    return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : n.substring(0, 2).toUpperCase();
};

const Perfil = ({ usuario }) => {
    const { showNotification } = useNotification();

    const [showDatos, setShowDatos] = useState(false);
    const [showModulos, setShowModulos] = useState(false);

    // Preview de PDF (resumen anual) — usa el dialog interno con iframe
    const [pdfPreview, setPdfPreview] = useState({ open: false, blob: null, titulo: '', filename: '' });
    const [generandoPdf, setGenerandoPdf] = useState(false);

    // Contraseña dialog
    const [pwDlg, setPwDlg] = useState(false);
    const [passwordActual, setPasswordActual] = useState('');
    const [passwordNueva, setPasswordNueva] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [showPwActual, setShowPwActual] = useState(false);
    const [showPwNueva, setShowPwNueva] = useState(false);
    const [showPwConfirm, setShowPwConfirm] = useState(false);
    const [loadingPw, setLoadingPw] = useState(false);

    const [avatarError, setAvatarError] = useState(false);
    const [fotoVersion, setFotoVersion] = useState(Date.now());
    const [esDirector, setEsDirector] = useState(false);
    const [esAutoridad, setEsAutoridad] = useState(false);
    const [showMiEquipo, setShowMiEquipo] = useState(false);
    const [showLicencias, setShowLicencias] = useState(true);
    const [showTareas, setShowTareas] = useState(false);
    const [showEjec, setShowEjec] = useState(false);
    const [tareasCount, setTareasCount] = useState(0);
    const autoExpandTareasRef = useRef(false);

    // Expandir automáticamente la primera vez que se detecten tareas pendientes
    useEffect(() => {
        if (tareasCount > 0 && !autoExpandTareasRef.current) {
            setShowTareas(true);
            autoExpandTareasRef.current = true;
        }
    }, [tareasCount]);

    // Chequear roles: director/subdirector + autoridad
    useEffect(() => {
        const token = localStorage.getItem('SIGIF_JWT_TOKEN');
        if (!token) return;
        fetch(nodeApiPath('personal/mi/equipo'), { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json()).then(r => setEsDirector(!!r.ok)).catch(() => { });
        fetch(nodeApiPath('personal/vista-ejecutiva'), { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json()).then(r => { if (r.ok) { setEsAutoridad(true); setShowEjec(true); } }).catch(() => { });
    }, []);
    const avatarSrc = (usuario?.id_personal || usuario?.dni)
        ? `${nodeApiPath(`personal/${usuario.id_personal || usuario.dni}/foto`)}?token=${localStorage.getItem('SIGIF_JWT_TOKEN')}&v=${fotoVersion}`
        : null;

    // Crop foto dialog
    const [cropDlg, setCropDlg] = useState({ open: false, file: null });
    const handleFotoFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!['image/jpeg', 'image/png'].includes(file.type)) { showNotification('Solo JPG o PNG', 'warning'); return; }
        setCropDlg({ open: true, file });
        e.target.value = '';
    };
    const subirFoto = async (croppedFile) => {
        try {
            const fd = new FormData();
            fd.append('file', croppedFile);
            const token = localStorage.getItem('SIGIF_JWT_TOKEN');
            const res = await fetch(nodeApiPath('personal/mi/foto'), {
                method: 'POST', body: fd, headers: { Authorization: `Bearer ${token}` },
            });
            const r = await res.json();
            if (r.ok) {
                showNotification('Foto actualizada', 'success');
                setFotoVersion(Date.now());
                setAvatarError(false);
                // Notificar a Header/otros componentes que la foto cambió
                window.dispatchEvent(new CustomEvent('sigif-foto-actualizada'));
            } else showNotification(r.mensaje || 'Error', 'error');
        } catch (e) { showNotification('Error: ' + e.message, 'error'); }
    };

    const handleCambiarPassword = async () => {
        if (!passwordActual || !passwordNueva || !passwordConfirm) { showNotification('Completá todos los campos', 'warning'); return; }
        if (passwordNueva.length < 6) { showNotification('Mínimo 6 caracteres', 'warning'); return; }
        if (passwordNueva !== passwordConfirm) { showNotification('Las contraseñas no coinciden', 'warning'); return; }
        setLoadingPw(true);
        try {
            const data = await cambiarPasswordJWT(passwordActual, passwordNueva);
            if (data?.ok) {
                showNotification('Contraseña cambiada', 'success');
                setPwDlg(false); setPasswordActual(''); setPasswordNueva(''); setPasswordConfirm('');
            } else showNotification(data?.mensaje || 'Error', 'error');
        } catch (err) { showNotification(err?.message || 'Error', 'error'); }
        setLoadingPw(false);
    };

    // Datos rápidos para chips
    const datos = [
        { icon: <BadgeIcon sx={{ fontSize: 14 }} />, label: `DNI ${usuario?.dni || '—'}` },
        { icon: <BadgeIcon sx={{ fontSize: 14 }} />, label: `CUIL ${fmtCUIL(usuario?.cuil)}` },
        usuario?.email && { icon: <EmailIcon sx={{ fontSize: 14 }} />, label: usuario.email },
        usuario?.telefono && { icon: <PhoneIcon sx={{ fontSize: 14 }} />, label: fmtPhone(usuario.telefono) },
        usuario?.celular && { icon: <PhoneIcon sx={{ fontSize: 14 }} />, label: fmtPhone(usuario.celular) },
    ].filter(Boolean);

    return (
        <div>
            {/* Header con título */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ color: '#fff' }}>Mi Perfil</Typography>
            </Box>

            {/* ═══ Cabecera: avatar + nombre + datos rápidos + botones ═══ */}
            <Paper sx={{ p: 3, borderRadius: BR, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Box sx={{ position: 'relative', cursor: 'pointer' }} component="label">
                        <Avatar
                            src={!avatarError && avatarSrc ? avatarSrc : undefined}
                            onError={() => setAvatarError(true)}
                            sx={{
                                width: 90, height: 90, border: `3px solid ${PRIMARY}`, fontSize: 32, fontWeight: 600,
                                transition: 'all 0.2s',
                                '&:hover': { opacity: 0.75 },
                            }}
                        >
                            {(avatarError || !avatarSrc) && getInitials(usuario?.nombre)}
                        </Avatar>
                        <Box sx={{
                            position: 'absolute', bottom: 0, right: 0, bgcolor: PRIMARY, color: '#fff',
                            borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: '2px solid #fff', cursor: 'pointer',
                        }}>
                            <CameraAltIcon sx={{ fontSize: 14 }} />
                        </Box>
                        <input type="file" hidden accept="image/jpeg,image/png" onChange={handleFotoFile} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>
                            {capitalize(usuario?.nombre)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                            {capitalize(usuario?.funcion || usuario?.rol) || 'Usuario'}
                            {usuario?.area ? ` · ${capitalize(usuario.area)}` : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {datos.map((d, i) => (
                                <Chip key={i} icon={d.icon} label={d.label} size="small" variant="outlined"
                                    sx={{ fontSize: '0.75rem', borderRadius: BR }} />
                            ))}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button variant="outlined" size="small" startIcon={<LockIcon />} onClick={() => setPwDlg(true)}
                            sx={{ textTransform: 'none', borderRadius: BR, borderColor: PRIMARY, color: PRIMARY }}>
                            Cambiar contraseña
                        </Button>
                        <Button variant="outlined" size="small" startIcon={generandoPdf ? <CircularProgress size={14} /> : <DownloadIcon />}
                            disabled={generandoPdf}
                            onClick={async () => {
                            try {
                                setGenerandoPdf(true);
                                const token = localStorage.getItem('SIGIF_JWT_TOKEN');
                                const ano = new Date().getFullYear();
                                const res = await fetch(nodeApiPath(`personal/mi/resumen-anual?ano=${ano}`), { headers: { Authorization: `Bearer ${token}` } });
                                const data = await res.json();
                                if (!data.ok) { showNotification(data.mensaje || 'Error', 'error'); return; }

                                const p = data.persona || {};
                                if (!p.Id) {
                                    showNotification('Tu usuario no tiene un legajo (Personal) asociado, por eso el resumen sale vacío. Pedile al administrador que vincule tu cuenta.', 'warning');
                                    return;
                                }
                                const a = data.asistencia || {};
                                const tipos = data.solicitudesPorTipo || {};
                                const totalHoras = Math.floor((a.MinTardanza || 0) / 60);
                                const totalMin = (a.MinTardanza || 0) % 60;

                                const html = `
                                    <div style="text-align:center;border-bottom:2px solid #0277bd;padding-bottom:10px;margin-bottom:20px">
                                      <div style="font-size:14pt;font-weight:bold;color:#0277bd">FISCALÍA DE ESTADO</div>
                                      <div style="font-size:10pt;color:#666">Provincia de San Juan</div>
                                    </div>
                                    <h2 style="text-align:center;font-size:13pt;margin:15px 0">RESUMEN ANUAL ${ano}</h2>
                                    <div style="margin-bottom:20px">
                                      <p><strong>Apellido y Nombre:</strong> ${(p.Apellido || '').toUpperCase()}, ${(p.Nombre || '').toUpperCase()}</p>
                                      <p><strong>DNI:</strong> ${p.Id || '—'} · <strong>CUIL:</strong> ${p.CUIL || '—'}</p>
                                      <p><strong>Función:</strong> ${p.Funcion || '—'} · <strong>Área:</strong> ${p.Area || '—'}</p>
                                    </div>
                                    <h3 style="color:#0277bd;font-size:11pt;border-bottom:1px solid #ccc;padding-bottom:4px">Asistencia</h3>
                                    <table style="width:100%;border-collapse:collapse;margin-bottom:20px">
                                      <tr><td style="padding:4px 8px;border:1px solid #ccc"><strong>Días registrados</strong></td><td style="padding:4px 8px;border:1px solid #ccc">${a.Total || 0}</td></tr>
                                      <tr><td style="padding:4px 8px;border:1px solid #ccc"><strong>Días normales</strong></td><td style="padding:4px 8px;border:1px solid #ccc">${a.Normales || 0}</td></tr>
                                      <tr><td style="padding:4px 8px;border:1px solid #ccc"><strong>Tardanzas</strong></td><td style="padding:4px 8px;border:1px solid #ccc">${a.Tardanzas || 0} (${totalHoras}h ${totalMin}m acumulados)</td></tr>
                                      <tr><td style="padding:4px 8px;border:1px solid #ccc"><strong>Ausencias</strong></td><td style="padding:4px 8px;border:1px solid #ccc">${a.Ausencias || 0}</td></tr>
                                    </table>
                                    <h3 style="color:#0277bd;font-size:11pt;border-bottom:1px solid #ccc;padding-bottom:4px">Licencias y Solicitudes</h3>
                                    <table style="width:100%;border-collapse:collapse;margin-bottom:20px">
                                      <thead><tr style="background:#f5f5f5"><th style="padding:6px 8px;border:1px solid #ccc;text-align:left">Tipo</th><th style="padding:6px 8px;border:1px solid #ccc">Total</th><th style="padding:6px 8px;border:1px solid #ccc">Finalizadas</th><th style="padding:6px 8px;border:1px solid #ccc">Días</th></tr></thead>
                                      <tbody>
                                      ${Object.entries(tipos).map(([tipo, v]) =>
                                        `<tr><td style="padding:4px 8px;border:1px solid #ccc">${tipo}</td><td style="padding:4px 8px;border:1px solid #ccc;text-align:center">${v.total}</td><td style="padding:4px 8px;border:1px solid #ccc;text-align:center">${v.finalizadas}</td><td style="padding:4px 8px;border:1px solid #ccc;text-align:center">${v.dias}</td></tr>`
                                      ).join('') || '<tr><td colspan="4" style="padding:8px;text-align:center;color:#999">Sin solicitudes en el año</td></tr>'}
                                      </tbody>
                                    </table>
                                    <h3 style="color:#0277bd;font-size:11pt;border-bottom:1px solid #ccc;padding-bottom:4px">Detalle de solicitudes</h3>
                                    <table style="width:100%;border-collapse:collapse;font-size:9pt">
                                      <thead><tr style="background:#f5f5f5"><th style="padding:4px 6px;border:1px solid #ccc">Tipo</th><th style="padding:4px 6px;border:1px solid #ccc">Desde</th><th style="padding:4px 6px;border:1px solid #ccc">Hasta</th><th style="padding:4px 6px;border:1px solid #ccc">Días</th><th style="padding:4px 6px;border:1px solid #ccc">Estado</th></tr></thead>
                                      <tbody>
                                      ${(data.solicitudes || []).map(s => {
                                          const d = (x) => new Date(x + 'T12:00:00').toLocaleDateString('es-AR');
                                          return `<tr><td style="padding:3px 6px;border:1px solid #ccc">${s.Tipo}</td><td style="padding:3px 6px;border:1px solid #ccc">${d(s.Fecha_Desde)}</td><td style="padding:3px 6px;border:1px solid #ccc">${d(s.Fecha_Hasta)}</td><td style="padding:3px 6px;border:1px solid #ccc;text-align:center">${s.Dias_Habiles}</td><td style="padding:3px 6px;border:1px solid #ccc">${s.Estado}</td></tr>`;
                                      }).join('') || '<tr><td colspan="5" style="padding:8px;text-align:center;color:#999">Sin solicitudes</td></tr>'}
                                      </tbody>
                                    </table>
                                    <p style="margin-top:30px;color:#999;font-size:9pt;text-align:right">Generado: ${new Date().toLocaleDateString('es-AR')}</p>
                                `;

                                const pdfRes = await fetch(nodeApiPath('pdf/from-html'), {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                    body: JSON.stringify({ html, filename: `Resumen_${p.Apellido}_${ano}`, orientation: 'portrait' }),
                                });
                                if (!pdfRes.ok) throw new Error('Error generando PDF');
                                const blob = await pdfRes.blob();
                                // Abrir en el PdfPreviewDialog interno (mismo iframe que el resto del sistema)
                                setPdfPreview({
                                    open: true, blob,
                                    titulo: `Resumen Anual ${ano} — ${p.Apellido}, ${p.Nombre}`,
                                    filename: `Resumen_Anual_${ano}.pdf`,
                                });
                            } catch (e) { showNotification('Error: ' + e.message, 'error'); }
                            finally { setGenerandoPdf(false); }
                        }}
                            sx={{ textTransform: 'none', borderRadius: BR, borderColor: '#2e7d32', color: '#2e7d32' }}>
                            Resumen {new Date().getFullYear()}
                        </Button>
                        <Button variant="text" size="small" startIcon={showDatos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            onClick={() => setShowDatos(!showDatos)}
                            sx={{ textTransform: 'none', color: '#666' }}>
                            {showDatos ? 'Ocultar datos' : 'Ver todos mis datos'}
                        </Button>
                    </Box>
                </Box>

                {/* Datos colapsados */}
                <Collapse in={showDatos}>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: PRIMARY, fontWeight: 600, mb: 1 }}>
                                <AccountCircleIcon fontSize="small" /> Datos Personales
                            </Typography>
                            <Grid container spacing={2}>
                                {[
                                    { label: 'Usuario', value: usuario?.usuario },
                                    { label: 'Email', value: usuario?.email },
                                    { label: 'DNI', value: usuario?.dni },
                                    { label: 'CUIL', value: fmtCUIL(usuario?.cuil) },
                                    { label: 'Teléfono', value: fmtPhone(usuario?.telefono) },
                                    { label: 'Celular', value: fmtPhone(usuario?.celular) },
                                ].map((f, i) => (
                                    <Grid key={i} size={{ xs: 12, md: 4 }}>
                                        <TextField label={f.label} value={f.value || '—'} fullWidth disabled size="small" variant="outlined" />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                        {(usuario?.domicilio_laboral || usuario?.matricula_provincial) && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: PRIMARY, fontWeight: 600, mb: 1 }}>
                                    <WorkIcon fontSize="small" /> Información Laboral
                                </Typography>
                                <Grid container spacing={2}>
                                    {usuario?.domicilio_laboral && <Grid size={{ xs: 12, md: 6 }}><TextField label="Domicilio Laboral" value={capitalize(usuario.domicilio_laboral)} fullWidth disabled size="small" /></Grid>}
                                    {usuario?.matricula_provincial && <Grid size={{ xs: 12, md: 6 }}><TextField label="Matrícula Provincial" value={usuario.matricula_provincial} fullWidth disabled size="small" /></Grid>}
                                </Grid>
                            </Box>
                        )}
                        {usuario?.modulos?.length > 0 && (
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 1, mb: 1 }}
                                    onClick={() => setShowModulos(!showModulos)}>
                                    <Typography variant="subtitle2" sx={{ color: PRIMARY, fontWeight: 600 }}>
                                        Módulos del Sistema ({usuario.modulos.length})
                                    </Typography>
                                    {showModulos ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </Box>
                                <Collapse in={showModulos}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                        {usuario.modulos.map((mod, idx) => (
                                            <Chip key={idx} label={mod.nombre} color="primary" variant="outlined" size="small" sx={{ borderRadius: BR }} />
                                        ))}
                                    </Box>
                                </Collapse>
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </Paper>

            {/* ═══ MIS TAREAS PENDIENTES (siempre visible) ═══ */}
            <Paper sx={{ p: 2, borderRadius: BR, mb: 3, borderLeft: `4px solid ${tareasCount > 0 ? '#e65100' : '#9e9e9e'}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', px: 1, py: 1 }}
                    onClick={() => setShowTareas(!showTareas)}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, color: tareasCount > 0 ? '#e65100' : '#666', flex: 1 }}>
                        <AssignmentTurnedInIcon /> Mis Tareas Pendientes
                        {tareasCount > 0 ? (
                            <Chip label={tareasCount} size="small" sx={{ bgcolor: '#e65100', color: '#fff', fontWeight: 700 }} />
                        ) : (
                            <Chip label="0" size="small" sx={{ bgcolor: '#e0e0e0', color: '#666', fontWeight: 600 }} />
                        )}
                    </Typography>
                    {showTareas ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
                <Collapse in={showTareas}>
                    <Box sx={{ p: 1, pt: 2 }}>
                        <MisTareasSeccion notify={showNotification} onCountChange={setTareasCount} />
                    </Box>
                </Collapse>
            </Paper>

            {/* ═══ VISTA EJECUTIVA (solo autoridades) ═══ */}
            {esAutoridad && (
                <Paper sx={{ p: 2, borderRadius: BR, mb: 3, borderLeft: '4px solid #6a1b9a' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', px: 1, py: 1 }}
                        onClick={() => setShowEjec(!showEjec)}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, color: '#6a1b9a', flex: 1 }}>
                            <TrendingUpIcon /> Vista Ejecutiva
                        </Typography>
                        {showEjec ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>
                    <Collapse in={showEjec}>
                        <Box sx={{ p: 1, pt: 2 }}>
                            <VistaEjecutiva />
                        </Box>
                    </Collapse>
                </Paper>
            )}

            {/* ═══ MI EQUIPO (solo director/subdirector) ═══ */}
            {esDirector && (
                <Paper sx={{ p: 2, borderRadius: BR, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', px: 1, py: 1 }}
                        onClick={() => setShowMiEquipo(!showMiEquipo)}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, color: PRIMARY, flex: 1 }}>
                            <PeopleIcon /> Mi Equipo
                        </Typography>
                        {showMiEquipo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>
                    <Collapse in={showMiEquipo}>
                        <Box sx={{ p: 1, pt: 2 }}>
                            <MiEquipo />
                        </Box>
                    </Collapse>
                </Paper>
            )}

            {/* ═══ MIS LICENCIAS ═══ */}
            <Paper sx={{ p: 2, borderRadius: BR }}>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', px: 1, py: 1 }}
                    onClick={() => setShowLicencias(!showLicencias)}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, color: PRIMARY, flex: 1 }}>
                        <BeachAccessIcon /> Mis Licencias y Vacaciones
                    </Typography>
                    {showLicencias ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
                <Collapse in={showLicencias}>
                    <Box sx={{ p: 1, pt: 2 }}>
                        <MisLicencias />
                    </Box>
                </Collapse>
            </Paper>

            {/* ═══ DIALOG: Crop foto ═══ */}
            <FotoCropDialog
                open={cropDlg.open}
                file={cropDlg.file}
                onClose={() => setCropDlg({ open: false, file: null })}
                onConfirm={subirFoto}
            />

            {/* ═══ DIALOG: Preview Resumen Anual PDF ═══ */}
            <PdfPreviewDialog
                open={pdfPreview.open}
                blob={pdfPreview.blob}
                titulo={pdfPreview.titulo}
                filename={pdfPreview.filename}
                onClose={() => setPdfPreview({ open: false, blob: null, titulo: '', filename: '' })}
            />

            {/* ═══ DIALOG: Cambiar contraseña ═══ */}
            <Dialog open={pwDlg} onClose={() => setPwDlg(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: BR } }}>
                <DialogTitle sx={{ bgcolor: '#e3f2fd', color: PRIMARY, fontWeight: 700, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockIcon /> Cambiar contraseña
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Contraseña actual" type={showPwActual ? 'text' : 'password'}
                            value={passwordActual} onChange={e => setPasswordActual(e.target.value)} size="small" fullWidth
                            InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowPwActual(!showPwActual)}>{showPwActual ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> }} />
                        <TextField label="Contraseña nueva" type={showPwNueva ? 'text' : 'password'}
                            value={passwordNueva} onChange={e => setPasswordNueva(e.target.value)} size="small" fullWidth
                            helperText="Mínimo 6 caracteres"
                            InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowPwNueva(!showPwNueva)}>{showPwNueva ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> }} />
                        <TextField label="Confirmar contraseña nueva" type={showPwConfirm ? 'text' : 'password'}
                            value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)} size="small" fullWidth
                            InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowPwConfirm(!showPwConfirm)}>{showPwConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> }} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setPwDlg(false); setPasswordActual(''); setPasswordNueva(''); setPasswordConfirm(''); }}
                        sx={{ textTransform: 'none' }} disabled={loadingPw}>Cancelar</Button>
                    <Button variant="contained" onClick={handleCambiarPassword} disabled={loadingPw}
                        startIcon={loadingPw ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <LockIcon />}
                        sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BR, '&:hover': { bgcolor: '#0d47a1' } }}>
                        {loadingPw ? 'Cambiando...' : 'Cambiar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Perfil;
